import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'

export type UserType = {
  id?: number
  email?: string
  remember_me_token?: string | null
  first_name?: string
  last_name?: string
  phone?: string
  address?: string | null
  is_verified?: string
  uuid?: string
  roles?: string
  agreement?: string
  onboarding?: string
  whatsapp_daily_time?: string
  enable_whatsapp_notification?: boolean
  created_at?: string
  updated_at?: string
} | null

// Membuat context dengan tipe default null
type UserContextType = {
  userData: UserType
  updateUser: (newUser: UserType) => void
  updateUserData: (newUser: Partial<UserType>) => void
  updateUserProfile: (data: any) => void
}

const UserContext = createContext<UserContextType>({
  userData: null,
  updateUser: () => { },
  updateUserData: () => { },
  updateUserProfile: () => { },
})

type UserProviderProps = {
  children: ReactNode
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  // const location = useLocation()
  const [userData, setUserData] = useState<UserType>(null)
  const [isLoading, setIsLoading] = useState(true) // Menambahkan state untuk loading

  useEffect(() => {
    const fetchUserData = async () => {
      const userDataString = localStorage.getItem('user')
      // Mengonversi data dari string ke objek JavaScript
      let userData: any
      if (userDataString !== null) {
        userData = JSON.parse(userDataString)
      }
      if (userData == null) {
        const path = location.pathname
        if (!path.startsWith('/admin') && !path.startsWith('/auth')) {
          navigate('/')
        }
      }
      setUserData(userData)
      setIsLoading(false) // Set loading menjadi false setelah data di-fetch
    }

    fetchUserData()
  }, [navigate])

  const updateUser = useCallback((newUser: UserType) => {
    setUserData(newUser)
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser))
    } else {
      localStorage.removeItem('user')
    }
  }, [])

  const updateUserData = useCallback((updates: Partial<UserType>) => {
    setUserData((currentUser) => {
      // Jika currentUser adalah null, mungkin Anda ingin menangani secara khusus atau mengembalikan null
      if (currentUser === null) {
        return null
      }

      const updatedUser = {
        ...currentUser,
        ...updates,
      }
      localStorage.setItem('user', JSON.stringify(updatedUser))
      return updatedUser
    })
  }, [])



  const updateUserProfile = useCallback((data: any) => {
    setUserData((currentUser) => {
      // Fallback to localStorage if currentUser is potentially stale or incomplete
      const storedUserStr = localStorage.getItem('user')
      const baseUser = storedUserStr ? JSON.parse(storedUserStr) : currentUser

      if (!baseUser) {
        return null
      }

      const updatedUser = {
        ...baseUser,
        first_name: data.first_name,
        last_name: data.last_name,
        address: data.address,
      }

      console.log('Updating profile. Base:', baseUser, 'Updates:', data, 'Result:', updatedUser)

      localStorage.setItem('user', JSON.stringify(updatedUser))
      return updatedUser
    })
  }, [])

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <UserContext.Provider
      value={{ userData, updateUser, updateUserData, updateUserProfile }}
    >
      {children}
    </UserContext.Provider>
  )
}

// Hook untuk menggunakan UserContext
export const useUser = () => useContext(UserContext)
